import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import { getCensusBeds, getBedOccupants } from "../../api/bedCensusApi";
import {
  describeBedCharge,
  getAllocatedAdmissionForPatient,
  raiseTransferRequest,
  searchPatients,
} from "../../api/bedTransferRequestApi";
import { describeAccrual, moveBedAccrual } from "../../api/autoChargeApi";

// Mirrors RetainBedModal's options — the same hold, just chosen up-front on the
// transfer rather than after the bed is already free.
const RETENTION_DURATION_OPTIONS = [
  { label: "2 hours", hours: 2 },
  { label: "6 hours", hours: 6 },
  { label: "12 hours", hours: 12 },
  { label: "24 hours", hours: 24 },
  { label: "48 hours", hours: 48 },
];

// Raise a bed transfer requisition (G12) — the formal approve-then-execute
// workflow, and the only way to transfer a bed now that the direct-execute
// edit_bed/cross_department_bed_transfer endpoints are retired (ADR 0003).
//
// When the caller already knows the patient (bed dashboard / bed allocation
// screens, which pass admissionId + fromBedCode), the modal opens straight
// to the transfer form. Otherwise it opens on a patient-search step
// (UHID/Aadhaar/mobile/name, requirement #3) that resolves the patient's
// current allocated admission + bed before showing the same form.
//
// The picker step ports in the pre-Epic-2 TransferBedDialog UX (same-ward/
// different-ward toggle, ward filter, vacant-bed list, auto-detected
// cross-department chip) instead of a flat "any available bed" dropdown —
// see docs/superpowers/specs/2026-07-09-bed-transfer-rework-design.md.
// The modal always fetches its own bed census, unfiltered. It used to accept
// the caller's already-loaded census (BedAllocation / BedDashboard via
// useBedCensusData) to save a round-trip, but that list is narrowed by the
// dashboard's Ward/Status/Room filters and patient search — so filtering the
// census silently emptied the destination-bed dropdown.
export default function RaiseTransferRequestModal({
  open,
  onClose,
  admissionId: admissionIdProp,
  fromBedCode: fromBedCodeProp,
  fromBed: fromBedProp,
  onRaised,
}) {
  const needsPatientSearch = !admissionIdProp || !fromBedCodeProp;

  const [step, setStep] = useState("search"); // "search" | "form"
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [resolvedAdmissionId, setResolvedAdmissionId] = useState("");
  const [resolvedFromBedCode, setResolvedFromBedCode] = useState("");
  const [resolvedPatientName, setResolvedPatientName] = useState("");

  const admissionId = admissionIdProp || resolvedAdmissionId;
  const fromBedCode = fromBedCodeProp || resolvedFromBedCode;

  const [beds, setBeds] = useState([]);
  const [admissionByBed, setAdmissionByBed] = useState({});
  const [bedsLoading, setBedsLoading] = useState(false);

  const fromBed = fromBedProp || beds.find((b) => b.code === fromBedCode) || null;

  const [scope, setScope] = useState("same_ward"); // "same_ward" | "other_ward"
  const [wardFilter, setWardFilter] = useState("");
  const [toBedCode, setToBedCode] = useState("");
  const [reason, setReason] = useState("");
  // No default: initiated_by drives billing downstream, so an unchosen value
  // must not silently resolve to "hospital". Submit stays disabled until set.
  const [initiatedBy, setInitiatedBy] = useState("");
  // Only asked (and only sent) when initiatedBy === "hospital" — see ADR 0008.
  const [hospitalTransferReason, setHospitalTransferReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Hold the patient's current bed for their return instead of freeing it.
  // Both beds are billed for as long as the hold is active, so this is off by
  // default and the cost is spelled out in the UI.
  const [retainFromBed, setRetainFromBed] = useState(false);
  const [retentionHours, setRetentionHours] = useState(24);
  const [retentionReason, setRetentionReason] = useState("");


  useEffect(() => {
    if (!open) return;
    setStep(needsPatientSearch ? "search" : "form");
    setSearchQuery("");
    setSearchResults([]);
    setResolvedAdmissionId("");
    setResolvedFromBedCode("");
    setResolvedPatientName("");
    setBeds([]);
    setAdmissionByBed({});
    setScope("same_ward");
    setWardFilter("");
    setToBedCode("");
    setReason("");
    setInitiatedBy("");
    setRetainFromBed(false);
    setRetentionHours(24);
    setRetentionReason("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lazy census fetch, deliberately unfiltered — the destination-bed list must
  // span every vacant bed in the facility regardless of what the dashboard
  // behind this modal is filtered to.
  useEffect(() => {
    if (!open || step !== "form") return;
    setBedsLoading(true);
    Promise.all([getCensusBeds({}), getBedOccupants()])
      .then(([bedRows, admissionRows]) => {
        setBeds(bedRows);
        // Keep the whole admission row, not just an occupied flag — it already
        // carries doctors.admitting, which is what surfaces the patient's
        // current admitting doctor below without a second round-trip.
        const map = {};
        (admissionRows || []).forEach((a) => {
          if (a.bed_id) map[a.bed_id] = a;
        });
        setAdmissionByBed(map);
      })
      .catch((err) => {
        console.error("Failed to load bed census for transfer picker:", err);
        setBeds([]);
        setAdmissionByBed({});
      })
      .finally(() => setBedsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  const vacantBeds = useMemo(() => {
    return (beds || []).filter(
      (b) => b.code !== fromBed?.code
        && !admissionByBed?.[b.code]
        && String(b.status || "").toLowerCase() === "available"
    );
  }, [beds, fromBed, admissionByBed]);

  // Department (Cardiology, Orthopaedics) > Ward (ICU, General, ITU, Private) >
  // Bed. The ward is room_category — the payload's ward_code/ward_name hold a
  // DEPARTMENT name and are never read here. See useBedCensusData's hierarchy
  // note.
  // A ward IS the bed type — the same notion the backend guard enforces
  // (main/ipd/transfer_eligibility.py). Reading room_category here instead would
  // let a user pick a bed this screen calls "same ward" that the server then
  // rejects, since the two disagree on beds that have no room category.
  const wardOf = (b) => b?.bed_type_code || "";
  const wardNameOf = (b) => b?.bed_type_name || b?.bed_type_code || "";

  const sameWardAsPatient = (b) => wardOf(b) === wardOf(fromBed);

  // Vacant beds in a DIFFERENT ward. Beds carry no department, so this is now
  // every other ward in the facility rather than only the patient's own
  // department's — which is the point of the decoupling: a free ICU bed is a
  // free ICU bed whoever created it.
  const otherWardBeds = useMemo(
    () => vacantBeds.filter((b) => wardOf(b) && wardOf(b) !== wardOf(fromBed)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vacantBeds, fromBed]
  );

  // Options come from the vacant list itself, so a ward with no free bed never
  // appears as a dead-end choice.
  const otherWards = useMemo(() => {
    const seen = new Map();
    otherWardBeds.forEach((b) => {
      const code = wardOf(b);
      if (code && !seen.has(code)) seen.set(code, wardNameOf(b));
    });
    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [otherWardBeds]);

  const scopedBeds = useMemo(() => {
    if (scope === "same_ward") {
      return vacantBeds.filter((b) => wardOf(b) && sameWardAsPatient(b));
    }
    // Different ward: the list stays empty until a ward is picked, so it can
    // never show every vacant bed in the hospital at once.
    if (!wardFilter) return [];
    return otherWardBeds.filter((b) => wardOf(b) === wardFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vacantBeds, otherWardBeds, scope, fromBed, wardFilter]);

  const toBed = scopedBeds.find((b) => b.code === toBedCode);

  // Does this move change the ward? That is the whole taxonomy now — it drives
  // the wire value below and the approval path that follows from it.
  const wardChanged =
    fromBed && toBed ? wardOf(fromBed) !== wardOf(toBed) : false;

  // The next-attending-doctor picker is GONE from this screen. It existed only for
  // the cross-department scope, and it listed doctors from the DESTINATION BED's
  // department — a bed has no department any more, so the list has no source and
  // the reason for the control has gone with it: moving a bed no longer moves a
  // patient between treating teams.
  //
  // Handing a patient to another doctor is Transfer Care (doctor console), and
  // moving them to another department is its own approval-gated action. The
  // backend still accepts new_admitting_doctor_id on a requisition (ADR 0007) —
  // that capability is untouched; this screen simply no longer offers it.
  // The wire value now says exactly what the user was shown. It used to need a
  // workaround: "within_ward" meant same-department-AND-same-bed-type on the
  // backend, so a same-ward bed-type change had to be sent as "cross_department"
  // or it passed approval and then hard-failed at execute. A ward IS the bed
  // type on both sides now, so the two agree and the workaround is gone.
  const transferType = wardChanged ? "cross_ward" : "within_ward";

  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query) return;
    setSearching(true);
    try {
      const patients = await searchPatients(query);
      setSearchResults(patients);
      if (!patients.length) toast.info("No patients found for that search.");
    } catch (err) {
      console.error("Patient search failed:", err);
      toast.error("Patient search failed.");
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handlePickPatient = async (patient) => {
    setSearching(true);
    try {
      const admissions = await getAllocatedAdmissionForPatient(patient.patient_id);
      const admission = admissions[0];
      if (!admission) {
        toast.error(`${patient.name || "This patient"} has no active bed allocation.`);
        return;
      }
      setResolvedAdmissionId(admission.admission_id);
      setResolvedFromBedCode(admission.bed_id);
      setResolvedPatientName(patient.name || "");
      setStep("form");
    } catch (err) {
      console.error("Failed to resolve patient's current admission:", err);
      toast.error("Could not resolve the patient's current bed.");
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!admissionId || !fromBedCode) {
      toast.error("Missing admission or current bed.");
      return;
    }
    if (!toBedCode) {
      toast.error("Please select a destination bed.");
      return;
    }
    if (!initiatedBy) {
      toast.error("Please select who initiated this transfer.");
      return;
    }
    if (initiatedBy === "hospital" && !hospitalTransferReason) {
      toast.error("Please say why the hospital is moving this patient — it decides how the move is billed.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await raiseTransferRequest({
        admissionId,
        fromBedCode,
        toBedCode,
        transferType,
        reason: reason.trim() || undefined,
        initiatedBy,
        hospitalTransferReason:
          initiatedBy === "hospital" ? hospitalTransferReason : undefined,
        retainFromBed,
        retentionExpiresAt: retainFromBed
          ? new Date(Date.now() + retentionHours * 60 * 60 * 1000).toISOString()
          : undefined,
        retentionReason: retainFromBed ? retentionReason.trim() || undefined : undefined,
      });
      if (data?.status === "Success") {
        toast.success(data.message || "Transfer requisition raised.");
        // A same-ward raise executes the transfer inline when the facility's
        // approval toggle is off (ADR 0007), so the bed charge lands on this
        // response too — not just on the explicit Execute in the list.
        const charge = describeBedCharge(data);
        if (charge) {
          toast[charge.severity](charge.text, {
            autoClose: charge.severity === "warning" ? false : undefined,
          });
        }
        // Only move accrual when that inline execute actually fired. A
        // requisition still sitting at "raised" has not moved the patient, and
        // stopping its accrual would leave them in a bed billing nothing.
        if (data?.data?.status === "transferred") {
          const accrual = describeAccrual(await moveBedAccrual(admissionId));
          if (accrual) {
            toast[accrual.severity](accrual.text, {
              autoClose: accrual.severity === "warning" ? false : undefined,
            });
          }
        }
        onRaised?.(data);
        onClose?.();
      } else {
        toast.error(data?.message || "Could not raise the requisition.");
      }
    } catch (err) {
      console.error("Raise transfer request failed:", err);
      toast.error(err?.response?.data?.message || "Could not raise the requisition.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {step === "search" ? (
        <>
          <DialogTitle sx={{ fontWeight: 700 }}>Find Patient to Transfer</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: "14px", mb: 1.5 }}>
              Search by UHID, Aadhaar, mobile, or full name.
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search UHID / Aadhaar / mobile / name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#888" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />
            {searching && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {!searching && searchResults.length > 0 && (
              <List dense sx={{ maxHeight: 260, overflowY: "auto" }}>
                {searchResults.map((p) => (
                  <ListItemButton key={p.patient_id} onClick={() => handlePickPatient(p)}>
                    <ListItemText
                      primary={p.name || p.patient_id}
                      secondary={[p.patient_id, p.phone_no].filter(Boolean).join(" · ")}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={searching}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSearch} disabled={searching || !searchQuery.trim()}>
              Search
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ fontWeight: 700 }}>Raise Bed Transfer Request</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: "14px", mb: 1.5 }}>
              {resolvedPatientName && <>{resolvedPatientName} — </>}
              From bed{" "}
              <strong>
                {fromBed
                  ? [fromBed.department_name, wardNameOf(fromBed), fromBed.name || fromBed.code]
                      .filter(Boolean)
                      .join(" · ")
                  : (fromBedCode || "-")}
              </strong>
              . A supervisor must approve this request before it is executed.
            </Typography>

            {bedsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={scope}
                  onChange={(_, v) => {
                    if (v) {
                      setScope(v);
                      setWardFilter("");
                      setToBedCode("");
                    }
                  }}
                  sx={{ mb: 1.5, flexWrap: "wrap" }}
                >
                  <ToggleButton value="same_ward">Same Ward</ToggleButton>
                  <ToggleButton value="other_ward">Different Ward</ToggleButton>
                </ToggleButtonGroup>

                {/* Different ward: pick the ward, then a vacant bed in it. Beds
                    carry no department, so every ward in the facility is offered. */}
                {scope === "other_ward" && (
                  <TextField
                    select
                    label="Ward"
                    size="small"
                    fullWidth
                    value={wardFilter}
                    onChange={(e) => {
                      setWardFilter(e.target.value);
                      setToBedCode("");
                    }}
                    helperText="Any other ward with a vacant bed."
                    sx={{ mb: 1.5 }}
                  >
                    <MenuItem value="">Select a ward</MenuItem>
                    {otherWards.map((w) => (
                      <MenuItem key={w.code} value={w.code}>
                        {w.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}


                <TextField
                  select
                  label="Transfer to vacant bed"
                  size="small"
                  fullWidth
                  value={toBedCode}
                  disabled={scope === "other_ward" && !wardFilter}
                  onChange={(e) => setToBedCode(e.target.value)}
                  helperText={
                    scope === "other_ward" && !wardFilter ? "Pick a ward first." : ""
                  }
                  sx={{ mb: 1.5 }}
                >
                  {scopedBeds.length === 0 && (
                    <MenuItem value="" disabled>
                      No vacant beds in this ward
                    </MenuItem>
                  )}
                  {scopedBeds.map((b) => (
                    <MenuItem key={b.code} value={b.code}>
                      {[wardNameOf(b), b.name || b.code].filter(Boolean).join(" · ")}
                    </MenuItem>
                  ))}
                </TextField>

                {toBed && (
                  <Chip
                    size="small"
                    sx={{ mb: 1.5 }}
                    variant="outlined"
                    color={wardChanged ? "warning" : "default"}
                    label={
                      wardChanged
                        ? `Cross-ward transfer · ${wardNameOf(fromBed)} → ${wardNameOf(toBed)}`
                        : `Same ward · ${wardNameOf(toBed)}`
                    }
                  />
                )}
              </>
            )}

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mb: 1.5 }}
            />

            <FormControl sx={{ display: "block", mb: 1.5 }}>
              <FormLabel sx={{ fontSize: "13px", fontWeight: 600 }}>
                Initiated by *
              </FormLabel>
              <Typography sx={{ fontSize: "12px", color: "#777", mb: 0.5 }}>
                Who asked for this transfer. This affects how the move is billed.
              </Typography>
              <RadioGroup
                value={initiatedBy}
                onChange={(e) => setInitiatedBy(e.target.value)}
              >
                <FormControlLabel
                  value="hospital"
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: "13px" }}>
                      Hospital-initiated
                      <Typography component="span" sx={{ fontSize: "12px", color: "#777", display: "block" }}>
                        A clinical or operational decision by the department.
                      </Typography>
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="patient"
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: "13px" }}>
                      Patient-initiated
                      <Typography component="span" sx={{ fontSize: "12px", color: "#777", display: "block" }}>
                        Requested by the patient or attendant (e.g. a room upgrade).
                      </Typography>
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* "Hospital-initiated" covers two cases that bill very differently
                (ADR 0008), so ask which. No default: defaulting to convenience
                would under-bill every ICU escalation, defaulting to clinical
                would charge patients for moves made for the hospital's benefit. */}
            {initiatedBy === "hospital" && (
              <FormControl sx={{ display: "block", mb: 1.5, pl: 1.5, borderLeft: "2px solid #e0e0e0" }}>
                <FormLabel sx={{ fontSize: "13px", fontWeight: 600 }}>
                  Why is the hospital moving this patient? *
                </FormLabel>
                <RadioGroup
                  value={hospitalTransferReason}
                  onChange={(e) => setHospitalTransferReason(e.target.value)}
                >
                  <FormControlLabel
                    value="clinical"
                    control={<Radio size="small" />}
                    label={
                      <Typography sx={{ fontSize: "13px" }}>
                        Clinical need
                        <Typography component="span" sx={{ fontSize: "12px", color: "#777", display: "block" }}>
                          The patient's condition requires this bed (e.g. escalation to ICU).
                          Billed at the new bed's rate.
                        </Typography>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="convenience"
                    control={<Radio size="small" />}
                    label={
                      <Typography sx={{ fontSize: "13px" }}>
                        Hospital convenience
                        <Typography component="span" sx={{ fontSize: "12px", color: "#777", display: "block" }}>
                          The hospital needs the original bed for other reasons.
                          Billed at the cheaper of the two beds — the patient isn't charged extra.
                        </Typography>
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            )}

            {/* Retain the original bed (ADR 0006). Off by default: it holds a
                bed out of service AND bills the patient for both beds, so it
                must be a deliberate choice, not a default. */}
            <FormControl sx={{ display: "block", mb: 1.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={retainFromBed}
                    onChange={(e) => setRetainFromBed(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                    Retain bed {fromBedCode || "—"} for this patient's return
                  </Typography>
                }
              />
              <Typography sx={{ fontSize: "12px", color: "#777", mb: retainFromBed ? 1 : 0 }}>
                Holds the original bed as "reserved" instead of releasing it —
                nobody else can be admitted to it. <strong>The patient is billed
                for both beds</strong> for as long as the hold lasts. The hold is
                released automatically if they are transferred back into it.
              </Typography>

              {retainFromBed && (
                <>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Hold for"
                    value={retentionHours}
                    onChange={(e) => setRetentionHours(Number(e.target.value))}
                    sx={{ mb: 1 }}
                  >
                    {RETENTION_DURATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.hours} value={opt.hours}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    label="Reason for holding the bed (optional)"
                    value={retentionReason}
                    onChange={(e) => setRetentionReason(e.target.value)}
                  />
                </>
              )}
            </FormControl>

          </DialogContent>
          <DialogActions>
            {needsPatientSearch && (
              <Button onClick={() => setStep("search")} disabled={submitting} sx={{ mr: "auto" }}>
                Back
              </Button>
            )}
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                submitting ||
                !toBedCode ||
                !initiatedBy ||
                (initiatedBy === "hospital" && !hospitalTransferReason)
              }
            >
              {submitting ? "Raising..." : "Raise Request"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
